import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import appState from '../state/AppStateContainer';

export const MAX_ENTRIES = 50;

type Entry = { value: unknown };

export type CachedRead<T> = {
  /** A copy of the last answer to the same read, which the caller may change. */
  cached: T | undefined;
  /** `changed` is false when the server answers what was cached. */
  fresh: Promise<{ value: T; changed: boolean }>;
};

const entries = new Map<string, Entry>();
// A read that a write overtook may hold what the write changed, so it is not remembered.
let generation = 0;

const sortedKeys = (_key: string, value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => (a < b ? -1 : 1)))
    : value;

// The org the request is rewritten to and the signed-in user, which Logga in som swaps in place.
const scope = () => `${appState._getCurrentOrgUrl()}|${appState.state?.token?.id ?? ''}`;

export const evaluationsCacheKey = (read: string, params: unknown) =>
  `${scope()}|${read}|${JSON.stringify(params ?? null, sortedKeys)}`;

export const clearEvaluationsCache = () => {
  entries.clear();
  generation += 1;
};

export const peekEvaluations = <T>(key: string): T | undefined => {
  const entry = entries.get(key);
  return entry && cloneDeep(entry.value as T);
};

const store = (key: string, value: unknown) => {
  entries.delete(key);
  entries.set(key, { value: cloneDeep(value) });
  const oldest = entries.keys().next().value;
  if (entries.size > MAX_ENTRIES && oldest !== undefined) entries.delete(oldest);
};

/** The last answer to the same read at once, and the server's answer behind it. */
export const readEvaluations = <T>(
  read: string,
  params: unknown,
  fetcher: () => Promise<T>,
  { skipCache = false } = {},
): CachedRead<T> => {
  const key = evaluationsCacheKey(read, params);
  const entry = skipCache ? undefined : entries.get(key);
  const cached = entry && cloneDeep(entry.value as T);
  const started = generation;
  const fresh = fetcher().then(value => {
    if (started === generation) store(key, value);
    return { value, changed: !entry || !isEqual(value, entry.value) };
  });
  return { cached, fresh };
};
